import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import type { MiniSiteTemplateId } from "@/lib/mini-site-templates";
import type {
  MiniSiteLink,
  MiniSiteSections,
  MiniSiteSocial,
} from "@/lib/mini-site.functions";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

export type BioAlbum = {
  id: string;
  title: string;
  artist: string;
  cover_url: string | null;
  price_brl: number | null;
  year: number | null;
  genres: string[];
  /** Nota do dono da coleção (mediana das faixas avaliadas) */
  rating?: number | null;
  rated_tracks?: number;
};


export type BioActivity = {
  id: string;
  type: string;
  created_at: string;
  album: {
    id: string;
    title: string;
    artist: string;
    cover_url: string | null;
  } | null;
};

export type BioLike = {
  post_id: string;
  created_at: string;
  album: {
    id: string;
    title: string;
    artist: string;
    cover_url: string | null;
  } | null;
  author_username: string | null;
};

export type PublicMiniSiteConfig = {
  template_id: MiniSiteTemplateId;
  title: string | null;
  subtitle: string | null;
  eyebrow: string | null;
  cover_url: string | null;
  sections: MiniSiteSections;
  links: MiniSiteLink[];
  social: MiniSiteSocial;
};

export type PublicBio = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  favorite_genres: string[];
  is_premium: boolean;
  xp: number;
  level: number;
  achievements_count: number;
  counts: {
    owned: number;
    wishlist: number;
    for_sale: number;
    for_trade: number;
    followers: number;
    loves: number;
  };
  owned: BioAlbum[];
  wishlist: BioAlbum[];
  for_sale: BioAlbum[];
  for_trade: BioAlbum[];
  mini_site: PublicMiniSiteConfig;
  activity: BioActivity[];
  likes: BioLike[];
  rituals: Array<{ id: string; album_title: string; artist: string; cover_url: string | null; moment_type: string; reflection: string | null; created_at: string }>;
} | null;

export const getPublicBio = createServerFn({ method: "GET" })
  .inputValidator((i: { username: string }) => i)
  .handler(async ({ data }): Promise<PublicBio> => {
    const sb = publicClient();
    const pageSize = 1000;
    const { data: p, error } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url, city, country, favorite_genres, bio_public")
      .eq("username", data.username.toLowerCase())
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!p) return null;
    if (p.bio_public === false) return null;
    const { data: bioText } = await sb.rpc("get_profile_bio", { _id: p.id });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const fetchAllAlbums = async () => {
      const rows: any[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data: page, error: pageError } = await supabaseAdmin
          .from("user_albums")
          .select(
            "id, discogs_id, title, artist, cover_url, status, listing, price_brl, sale_price, year, genres",
          )
          .eq("user_id", p.id)
          .order("created_at", { ascending: false })
          .range(from, from + pageSize - 1);
        if (pageError) throw new Error(pageError.message);
        rows.push(...(page ?? []));
        if (!page || page.length < pageSize) break;
      }
      return rows;
    };

    const fetchMyRatings = async () => {
      const rows: { discogs_id: string; rating: number }[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data: page } = await supabaseAdmin
          .from("album_track_ratings")
          .select("discogs_id, rating")
          .eq("user_id", p.id)
          .range(from, from + pageSize - 1);
        rows.push(...((page ?? []) as { discogs_id: string; rating: number }[]));
        if (!page || page.length < pageSize) break;
      }
      return rows;
    };

    const [albumsRes, followersRes, lovesRes, premRes, xpRes, achRes, msRes, ratingRows, ritualsRes] = await Promise.all([
      fetchAllAlbums(),
      supabaseAdmin
        .from("follows")
        .select("follower_id", { count: "exact", head: true })
        .eq("following_id", p.id),
      supabaseAdmin.from("profile_loves").select("user_id", { count: "exact", head: true }).eq("profile_id", p.id),
      supabaseAdmin.rpc("has_premium", { _user: p.id }),
      supabaseAdmin.from("user_xp").select("xp, level").eq("user_id", p.id).maybeSingle(),
      supabaseAdmin
        .from("user_achievements")
        .select("achievement_key", { count: "exact", head: true })
        .eq("user_id", p.id),
      supabaseAdmin
        .from("mini_sites")
        .select("template_id, title, subtitle, eyebrow, cover_url, sections, links, social, is_public")
        .eq("user_id", p.id)
        .maybeSingle(),
      fetchMyRatings(),
      supabaseAdmin.from("ritual_moments").select("id, album_title, artist, cover_url, moment_type, reflection, created_at").eq("user_id", p.id).eq("is_public", true).order("created_at", { ascending: false }).limit(60),
    ]);

    // ── Atividades recentes do colecionador
    const { data: activityPosts } = await supabaseAdmin
      .from("feed_posts")
      .select("id, type, created_at, album_id")
      .eq("user_id", p.id)
      .order("created_at", { ascending: false })
      .limit(24);

    // ── Curtidas dele em posts da comunidade
    const { data: likeRows } = await supabaseAdmin
      .from("post_likes")
      .select("post_id, created_at")
      .eq("user_id", p.id)
      .order("created_at", { ascending: false })
      .limit(24);

    const likedPostIds = (likeRows ?? []).map((l) => l.post_id);
    const { data: likedPosts } = likedPostIds.length
      ? await supabaseAdmin
          .from("feed_posts")
          .select("id, user_id, album_id")
          .in("id", likedPostIds)
      : { data: [] as { id: string; user_id: string; album_id: string | null }[] };

    const extraAlbumIds = [
      ...new Set(
        [
          ...(activityPosts ?? []).map((a) => a.album_id),
          ...(likedPosts ?? []).map((a) => a.album_id),
        ].filter(Boolean) as string[],
      ),
    ];
    const { data: extraAlbums } = extraAlbumIds.length
      ? await supabaseAdmin
          .from("user_albums")
          .select("id, title, artist, cover_url")
          .in("id", extraAlbumIds)
      : { data: [] as { id: string; title: string; artist: string; cover_url: string | null }[] };
    const extraAlbumMap = new Map((extraAlbums ?? []).map((a) => [a.id, a]));

    const likedAuthorIds = [...new Set((likedPosts ?? []).map((a) => a.user_id))];
    const { data: likedAuthors } = likedAuthorIds.length
      ? await supabaseAdmin.from("profiles").select("id, username").in("id", likedAuthorIds)
      : { data: [] as { id: string; username: string }[] };
    const likedAuthorMap = new Map((likedAuthors ?? []).map((a) => [a.id, a.username]));
    const likedPostMap = new Map((likedPosts ?? []).map((a) => [a.id, a]));

    const activity: BioActivity[] = (activityPosts ?? []).map((a) => ({
      id: a.id,
      type: a.type,
      created_at: a.created_at,
      album: a.album_id ? extraAlbumMap.get(a.album_id) ?? null : null,
    }));

    const likes: BioLike[] = (likeRows ?? []).map((l) => {
      const post = likedPostMap.get(l.post_id);
      return {
        post_id: l.post_id,
        created_at: l.created_at,
        album: post?.album_id ? extraAlbumMap.get(post.album_id) ?? null : null,
        author_username: post ? likedAuthorMap.get(post.user_id) ?? null : null,
      };
    });

    // Nota do dono por álbum = mediana das notas que ele deu às faixas
    const ratingByAlbum = new Map<string, number[]>();
    for (const r of ratingRows) {
      if (!r?.discogs_id) continue;
      const arr = ratingByAlbum.get(r.discogs_id) ?? [];
      arr.push(Number(r.rating));
      ratingByAlbum.set(r.discogs_id, arr);
    }
    const medianOf = (values: number[]) => {
      const v = [...values].sort((a, b) => a - b);
      const mid = Math.floor(v.length / 2);
      const m = v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
      return Math.round(m * 10) / 10;
    };

    const all = albumsRes ?? [];
    const toAlbum = (r: typeof all[number]): BioAlbum => {
      const values = ratingByAlbum.get((r as { discogs_id?: string }).discogs_id ?? "") ?? [];
      return {
        id: r.id,
        title: r.title,
        artist: r.artist,
        cover_url: r.cover_url,
        price_brl: (r.sale_price ?? r.price_brl) as number | null,
        year: (r as { year: number | null }).year ?? null,
        genres: Array.isArray((r as { genres: string[] | null }).genres) ? (r as { genres: string[] }).genres : [],
        rating: values.length ? medianOf(values) : null,
        rated_tracks: values.length,
      };
    };


    const owned = all.filter((r) => r.status === "owned").map(toAlbum);
    const wishlist = all.filter((r) => r.status === "wishlist").map(toAlbum);
    const for_sale = all.filter((r) => r.listing === "for_sale").map(toAlbum);
    const for_trade = all.filter((r) => r.listing === "for_trade").map(toAlbum);

    const ms = msRes.data;
    // Mini site can also be flagged private; honor either gate
    if (ms && ms.is_public === false) return null;

    const mini_site: PublicMiniSiteConfig = {
      template_id: (ms?.template_id ?? "diario") as MiniSiteTemplateId,
      title: ms?.title ?? null,
      subtitle: ms?.subtitle ?? null,
      eyebrow: ms?.eyebrow ?? null,
      cover_url: ms?.cover_url ?? null,
      sections: {
        collection: true,
        wishlist: true,
        reviews: true,
        links: true,
        about: true,
        ...((ms?.sections ?? {}) as MiniSiteSections),
      },
      links: (Array.isArray(ms?.links) ? ms!.links : []) as MiniSiteLink[],
      social: (ms?.social ?? {}) as MiniSiteSocial,
    };

    const { bio_public: _bp, ...rest } = p;
    return {
      ...rest,
      bio: (bioText as string | null) ?? null,
      is_premium: !!premRes.data,
      xp: xpRes.data?.xp ?? 0,
      level: xpRes.data?.level ?? 1,
      achievements_count: achRes.count ?? 0,
      counts: {
        owned: owned.length,
        wishlist: wishlist.length,
        for_sale: for_sale.length,
        for_trade: for_trade.length,
        followers: followersRes.count ?? 0,
        loves: lovesRes.count ?? 0,
      },
      owned,
      wishlist,
      for_sale,
      for_trade,
      mini_site,
      activity,
      likes,
      rituals: ritualsRes.data ?? [],
    };
  });

export const getMyBioPublic = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("profiles")
      .select("bio_public")
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { bio_public: data?.bio_public ?? true };
  });

export const setBioPublic = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { bio_public: boolean }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({ bio_public: data.bio_public })
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true, bio_public: data.bio_public };
  });
